import {
  Container, Section, SectionHeading, Reveal, ImageFrame, Eyebrow,
} from "@/components/common";
import { Icon, CTABand } from "@/components/ui-extras";
import { PageHero } from "@/components/ui-extras";
import { ABOUT_SECTIONS, IMG, img } from "@/data/content";

const HIGHLIGHTS = [
  { icon: "CalendarClock", label: "Established", value: "Since 2010" },
  { icon: "Award", label: "Recognition", value: "Star Export House" },
  { icon: "Globe2", label: "Presence", value: "ME · Africa · SE Asia" },
  { icon: "Handshake", label: "Focus", value: "Long-Term Buyers" },
];

const About = () => {
  return (
    <div>
      <PageHero
        eyebrow="About Prince Trading Impex"
        title="An Export House Built on Trust & Discipline"
        intro="Prince Trading Impex was established with a clear focus on building dependable international trade relationships in agricultural commodities — combining Indian sourcing strength with disciplined export execution."
        image={img(IMG.riceField, 1600)}
      />

      {/* Highlights */}
      <section className="bg-paper">
        <Container className="py-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {HIGHLIGHTS.map((h, i) => (
              <Reveal key={h.label} delay={i * 0.05}>
                <div className="rounded-[var(--radius)] bg-white p-6 h-full" style={{ border: "1px solid var(--pt-border-gold)", boxShadow: "var(--shadow-soft)" }}>
                  <Icon name={h.icon} size={22} color="#3ABBB0" />
                  <p className="mt-4 font-sans text-xs uppercase tracking-[0.16em]" style={{ color: "var(--pt-muted)" }}>{h.label}</p>
                  <p className="mt-1 font-serif text-xl" style={{ color: "var(--pt-navy)" }}>{h.value}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Intro narrative with image */}
      <Section className="bg-white">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <Reveal className="lg:col-span-6">
              <SectionHeading
                eyebrow="Who We Are"
                title="Indian Sourcing Strength, Disciplined Global Execution"
                intro="The company works with global buyers across rice, tea, and essential commodity segments. Our approach is built on consistency, product reliability, transparent documentation, and long-term buyer relationships."
              />
              <p className="mt-5 font-sans text-base leading-relaxed" style={{ color: "var(--pt-muted)" }}>
                With experience across Middle Eastern, African, and South-East Asian markets, Prince Trading Impex continues to expand its commodity portfolio while maintaining a strong focus on trust and execution.
              </p>
            </Reveal>
            <Reveal className="lg:col-span-6" delay={0.1}>
              <ImageFrame src={img(IMG.juteSacks, 900)} alt="Indian agricultural commodity sourcing — premium grain sacks ready for export" className="aspect-[4/3]" />
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* Certification credibility block */}
      <Section className="bg-white" dense>
        <Container>
          <Reveal>
            <div
              data-testid="about-iso-block"
              className="rounded-[var(--radius)] p-8 sm:p-10 lg:p-12"
              style={{ background: "linear-gradient(150deg,#E8FFF6,#DDFCF5)", border: "1px solid #B8EFE3", boxShadow: "0 14px 40px rgba(58,187,176,0.14)" }}
            >
              <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
                <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl" style={{ background: "linear-gradient(140deg,#3ABBB0,#41E19A)", boxShadow: "0 10px 26px rgba(58,187,176,0.4)" }}>
                  <Icon name="BadgeCheck" size={40} color="#FFFFFF" />
                </span>
                <div>
                  <span className="font-sans text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: "#3ABBB0" }}>ISO 22000:2018 Certified</span>
                  <h2 className="mt-2 font-serif text-2xl sm:text-3xl font-semibold leading-tight" style={{ color: "#063B36" }}>
                    Certified for Food Safety & Quality Discipline
                  </h2>
                  <p className="mt-4 max-w-3xl font-sans text-base leading-relaxed" style={{ color: "#2f6b62" }}>
                    Prince Trading Impex is ISO 22000:2018 Certified, reflecting its commitment to food safety management, quality discipline, sourcing reliability, and structured handling of agricultural commodity exports.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </Container>
      </Section>

      {/* Detailed sections */}
      <Section className="bg-paper" dense>
        <Container>
          <SectionHeading center eyebrow="Our Foundation" title="What Defines Prince Trading Impex" className="mb-14" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ABOUT_SECTIONS.map((s, i) => (
              <Reveal key={s.title} delay={(i % 2) * 0.06}>
                <div className="h-full rounded-[var(--radius)] bg-white p-7" style={{ border: "1px solid var(--pt-border)", boxShadow: "var(--shadow-soft)" }}>
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-2xl gold-text-gradient">0{i + 1}</span>
                    <span className="h-px flex-1" style={{ background: "var(--pt-border-gold)" }} />
                  </div>
                  <h3 className="mt-4 font-serif text-2xl" style={{ color: "var(--pt-navy)" }}>{s.title}</h3>
                  <p className="mt-3 font-sans text-sm leading-relaxed" style={{ color: "var(--pt-muted)" }}>{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      <CTABand
        heading="Build a long-term supply partnership."
        subtext="Connect with our team to discuss your commodity requirements and trade terms."
      />
    </div>
  );
};

export default About;
